// @ts-ignore
import { getFormStateStorageKey } from "@pathwright/ui/src/components/form/form-state/useFormStateStorage"

import useLocalStorage from "@pathwright/ui/src/components/hooks/useLocalStorage"
import usePreviousEffect from "@pathwright/ui/src/components/hooks/usePreviousEffect"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import Modal from "@pathwright/ui/src/components/modal/Modal"
import classnames from "classnames"
import merge from "lodash/merge"
import { useEffect, useState } from "react"
import styled from "styled-components"
import { useCertificateContext } from "../context/CertificateContext"
import useCertificatePreview from "../hooks/useCertificatePreview"
import useCertificatePreviewParam from "../hooks/useCertificatePreviewParam"
import { useWeavedBaseVariables } from "../variables/variables"

const Container = styled.div<{ $loading: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-width: 100vw;
  position: relative;
  z-index: 999999999;

  embed {
    min-height: 100vh;
    min-width: 100vw;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  embed:first-of-type {
    z-index: 1;
  }

  embed:last-of-type {
    z-index: 2;
    ${(p) => (p.$loading ? "opacity: 0;" : "")}
  }
`

const useCertificateFormStateStorage = () => {
  const { certificateId, certificateScope } = useCertificateContext()
  const baseKeyParts = ["certificate", certificateId, certificateScope]
  const initalValue = {}
  const options = { sync: true }

  const [certificateFormState] = useLocalStorage(
    getFormStateStorageKey(baseKeyParts),
    initalValue,
    options
  )

  return certificateFormState ? certificateFormState.values : null
}

const _useCertificatePreview = () => {
  const formState = useCertificateFormStateStorage()
  // Get the default preview for a certificate, necessary for when
  // there does not yet exist any form state storage.
  const [previewData] = useCertificatePreviewParam()
  const defaultValues = previewData || {}
  const values = merge(formState || defaultValues, {
    baseVariables: useWeavedBaseVariables()
  })
  return useCertificatePreview(values)
}

// NOTE: we render the PDF using ane embed due to known issuew with rendering
// PDFs in Safari when using an iframe.
// See issue: https://linear.app/pathwright/issue/SUP-1282/live-preview-of-a-certificate-of-completion-is-itty-bitty
const CertificatePreview = ({ className }: { className?: string }) => {
  const _certificatePreview = _useCertificatePreview()
  const certificatePreview = _certificatePreview
    ? `${_certificatePreview}&download=0`
    : ""

  // Track current and previous preview urls for handling loading states.
  const [previews, setPreviews] = useState<{
    previous: string
    current: string
  }>({ previous: "", current: "" })

  // Only updating the previous preview when the current preview changes
  // as long as we're not loading. Ideally would not rely on the usePreviousEffect
  // as it seems to add more complexity than necessary, but alternative solutions
  // have introduced some slightly less desireble loading UX.
  usePreviousEffect(
    ([prevCertificatePreview]: [string]) => {
      setPreviews({
        previous: prevCertificatePreview,
        current: certificatePreview
      })
    },
    [certificatePreview || previews.current]
  )

  // Loading state is managed as best as possible. We render 2 embeds + 1 iframe.
  // The iframe is never visible, only used to handle when the PDF is loaded.
  // The first embed is the previous preview, which is always visible.
  // The second embed is the current preview, which is only visible once the
  // iframe has loaded. This is to avoid the flicker of the PDF loading.
  // We then add an additional timeout of 300 ms to account for the embed rendering.
  const [loading, setLoading] = useState(true)
  const handleLoad = () => {
    setTimeout(() => {
      setLoading(false)
    }, 300)
  }

  useEffect(() => {
    setLoading(true)
  }, [previews.current])

  return previews.current ? (
    <Container
      className={classnames("CertificatePreview", className)}
      $loading={loading}
    >
      {!!previews.previous && (
        <embed key={previews.previous} src={previews.previous} />
      )}
      <embed
        key={previews.current}
        src={previews.current}
        title="PDF Preview"
        width="100%"
        height="100%"
      />
      {/* Load handler. Since embeds don't reliably handle loading state,
       we can defer that logic to this hidden iframe, only rendering the current
       PDF embed when the iframe has loaded. */}
      <iframe src={previews.current} width={0} height={0} onLoad={handleLoad} />
      {loading &&
        // NOTE: we render loading state, but might not improve UX.
        // Would have to be done in a non-intrusive way.
        null}
    </Container>
  ) : (
    <Modal>
      <LoadingCircle center inverted />
    </Modal>
  )
}

CertificatePreview.displayName = "CertificatePreview"

export default CertificatePreview
